import {
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { UrlApi } from "../../../services/apirest";
import { authHeader } from "../../../services/services";
import { ESTADOS_META as META, ORDEN_ESTADOS as ORDEN_FILTROS } from "./arcaEstados";

const fmtImporte = (v) =>
  v == null
    ? ""
    : new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);

const soloDigitos = (v) => (v == null ? "" : String(v).replace(/\D/g, ""));

// Clasifica una fila. Prioriza el `estado` granular del backend (§4.7). Si no viene, deriva del
// desglose `falta` ({ proveedor, condicion_venta, imputacion, tipo, fecha, total }); y si tampoco,
// cae al esquema viejo (completo/incompleto + id_proveedor null → alta de proveedor).
const clasificar = (r) => {
  if (r.estado === "registrado" || r.procesado || r.id_comprobante != null)
    return "registrado";
  if (r.estado && META[r.estado]) return r.estado;

  const f = r.falta;
  if (f && typeof f === "object") {
    if (f.proveedor) return "sin_proveedor";
    if (f.tipo || f.fecha || f.total) return "falta_datos_factura";
    if (f.condicion_venta) return "falta_condicion_venta";
    if (f.imputacion) return "falta_imputacion";
    return "completo";
  }

  if (
    Object.prototype.hasOwnProperty.call(r, "id_proveedor") &&
    r.id_proveedor == null
  )
    return "sin_proveedor";
  return "falta_datos_factura";
};

const ListadoPendientesArca = forwardRef(function ListadoPendientesArca(
  { idEmpresa, onCargar, onEncontrado, onCargarManual, puedeCargar = false },
  ref
) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqCuit, setBusqCuit] = useState("");
  const [busqFecha, setBusqFecha] = useState("");
  const [busqPv, setBusqPv] = useState("");
  const [busqNro, setBusqNro] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const ultimoEncontrado = useRef(null);

  const cuitBusc = soloDigitos(busqCuit);
  const busquedaActiva = !!(cuitBusc || busqFecha || busqPv || busqNro);
  const cuatroCompletos = !!(cuitBusc && busqFecha && busqPv && busqNro);

  // Filtro principal: los 4 datos del comprobante (CUIT + fecha + PV + Nº), client-side
  // sobre el staging ya traído. CUIT compara por dígitos; fecha exacta por fecha_emision;
  // PV/Nº por prefijo para que filtre progresivamente mientras se tipea.
  const filasBuscadas = useMemo(
    () =>
      rows.filter((r) => {
        if (cuitBusc && !soloDigitos(r.cuit_emisor).includes(cuitBusc)) return false;
        if (busqFecha && r.fecha_emision !== busqFecha) return false;
        if (busqPv && !String(r.punto_venta ?? "").startsWith(busqPv)) return false;
        if (busqNro && !String(r.nro_desde ?? "").startsWith(busqNro)) return false;
        return true;
      }),
    [rows, cuitBusc, busqFecha, busqPv, busqNro]
  );

  const conteos = useMemo(() => {
    const acc = {};
    filasBuscadas.forEach((r) => {
      const k = clasificar(r);
      acc[k] = (acc[k] || 0) + 1;
    });
    return acc;
  }, [filasBuscadas]);

  const filas = useMemo(
    () =>
      filasBuscadas.filter(
        (r) => filtroEstado === "todos" || clasificar(r) === filtroEstado
      ),
    [filasBuscadas, filtroEstado]
  );

  // Pendientes cargables (no registrados) dentro del resultado de búsqueda.
  const pendientesBuscados = useMemo(
    () => filasBuscadas.filter((r) => clasificar(r) !== "registrado"),
    [filasBuscadas]
  );

  const sinResultados = busquedaActiva && filasBuscadas.length === 0;

  // Con los 4 campos completos y exactamente 1 pendiente, se abre el popup (una sola vez
  // por fila; se rearma si cambia la búsqueda).
  useEffect(() => {
    if (!puedeCargar) return;
    if (cuatroCompletos && pendientesBuscados.length === 1) {
      const row = pendientesBuscados[0];
      if (ultimoEncontrado.current !== row.id) {
        ultimoEncontrado.current = row.id;
        onEncontrado?.(row);
      }
    } else {
      ultimoEncontrado.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuatroCompletos, pendientesBuscados, puedeCargar]);

  const limpiarBusqueda = () => {
    setBusqCuit("");
    setBusqFecha("");
    setBusqPv("");
    setBusqNro("");
  };

  const obtener = () => {
    if (idEmpresa == null) return;
    setLoading(true);
    // Pendientes (sin convertir) + procesados (ya convertidos, para trazabilidad "Registrado").
    // /procesados es best-effort: si no está disponible, se muestran solo los pendientes.
    Promise.all([
      fetch(UrlApi + `/comprobantes_arca/pendientes?id_empresa=${idEmpresa}`, {
        headers: authHeader(),
      })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
      fetch(UrlApi + `/comprobantes_arca/procesados?id_empresa=${idEmpresa}`, {
        headers: authHeader(),
      })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
    ])
      .then(([pend, proc]) => {
        const a = Array.isArray(pend) ? pend : [];
        const b = Array.isArray(proc) ? proc : [];
        setRows([...a, ...b]);
      })
      .catch((e) => console.error("Error pendientes ARCA:", e))
      .finally(() => setLoading(false));
  };

  useEffect(obtener, [idEmpresa]);
  useImperativeHandle(ref, () => ({ refrescar: obtener }));

  const columns = useMemo(
    () => [
      { header: "Tipo", accessorKey: "tipo_comprobante_arca", size: 70 },
      { header: "P. Venta", accessorKey: "punto_venta", size: 80 },
      { header: "Número", accessorKey: "nro_desde", size: 90 },
      {
        header: "Proveedor",
        accessorFn: (r) => r.denominacion_emisor ?? "",
        id: "proveedor",
        size: 220,
      },
      { header: "CUIT", accessorKey: "cuit_emisor", size: 120 },
      {
        header: "Emisión",
        accessorFn: (r) =>
          r.fecha_emision ? moment(r.fecha_emision).format("DD-MM-YYYY") : "",
        id: "fecha_emision",
        size: 100,
      },
      {
        header: "Cargado",
        accessorFn: (r) =>
          r.fecha_carga ? moment(r.fecha_carga).format("DD-MM-YYYY") : "",
        id: "fecha_carga",
        size: 100,
      },
      {
        header: "Total",
        accessorFn: (r) => fmtImporte(r.imp_total),
        id: "total",
        size: 120,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: "Estado",
        accessorFn: (r) => (META[clasificar(r)] || META.falta_datos_factura).label,
        id: "estado",
        size: 130,
        Cell: ({ row }) => {
          const cfg = META[clasificar(row.original)] || META.falta_datos_factura;
          const chip = (
            <Chip
              size="small"
              label={cfg.label}
              color={cfg.color}
              variant={cfg.variant}
            />
          );
          return cfg.tip ? (
            <Tooltip title={cfg.tip} arrow enterDelay={0} enterNextDelay={0}>
              {chip}
            </Tooltip>
          ) : (
            chip
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="CUIT"
            size="small"
            value={busqCuit}
            onChange={(e) => setBusqCuit(e.target.value.replace(/\D/g, ""))}
            inputProps={{ inputMode: "numeric", maxLength: 11 }}
            placeholder="Sólo números"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <TextField
            label="Fecha del comprobante"
            type="date"
            size="small"
            value={busqFecha}
            onChange={(e) => setBusqFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Punto de venta"
            size="small"
            value={busqPv}
            onChange={(e) => setBusqPv(e.target.value.replace(/\D/g, ""))}
            inputProps={{ inputMode: "numeric", maxLength: 5 }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130 }}
          />
          <TextField
            label="Número"
            size="small"
            value={busqNro}
            onChange={(e) => setBusqNro(e.target.value.replace(/\D/g, ""))}
            inputProps={{ inputMode: "numeric", maxLength: 8 }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          {busquedaActiva && (
            <Button size="small" color="error" onClick={limpiarBusqueda}>
              Limpiar
            </Button>
          )}
        </Stack>
        {!sinResultados && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {ORDEN_FILTROS.filter((k) => conteos[k]).map((k) => {
              const btn = (
                <ToggleButton
                  key={k}
                  size="small"
                  value={k}
                  selected={filtroEstado === k}
                  color={META[k].color === "default" ? "standard" : META[k].color}
                  onChange={() => setFiltroEstado(filtroEstado === k ? "todos" : k)}
                >
                  {META[k].label} ({conteos[k]})
                </ToggleButton>
              );
              return META[k].tip ? (
                <Tooltip key={k} title={META[k].tip} arrow enterDelay={0} enterNextDelay={0}>
                  {btn}
                </Tooltip>
              ) : (
                btn
              );
            })}
          </Stack>
        )}
      </Stack>

      {sinResultados ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No se encontró ningún comprobante con esos datos.
          </Typography>
          {puedeCargar && (
            <Button
              variant="contained"
              onClick={() =>
                onCargarManual?.({
                  cuit: cuitBusc,
                  fecha: busqFecha,
                  pv: busqPv,
                  nro: busqNro,
                })
              }
            >
              Carga manual
            </Button>
          )}
        </Box>
      ) : (
      <MaterialReactTable
        columns={columns}
        data={filas}
        state={{ isLoading: loading }}
        localization={MRT_Localization_ES}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableHiding={false}
        enableColumnFilters={false}
        enablePinning
        displayColumnDefOptions={{
          "mrt-row-actions": {
            size: 110,
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
          },
        }}
        initialState={{
          density: "compact",
          pagination: { pageSize: 10, pageIndex: 0 },
          columnPinning: { right: ["estado", "mrt-row-actions"] },
        }}
        enableRowActions={puedeCargar}
        positionActionsColumn="last"
        renderRowActions={({ row }) => {
          const registrado = clasificar(row.original) === "registrado";
          return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                size="small"
                variant="contained"
                disabled={registrado}
                color="primary"
                onClick={() => onCargar?.(row.original)}
              >
                {registrado ? "Registrado" : "Completar"}
              </Button>
            </Box>
          );
        }}
        muiTablePaperProps={{ sx: { borderRadius: 2, border: "1px solid #e0e0e0" } }}
      />
      )}
    </Box>
  );
});

export default ListadoPendientesArca;
